import { useState, useEffect, type FormEvent } from 'react';
import {
  SS27_LINES, type SS27Line, type ObjetivosByLine,
  emptyObjetivos, fetchObjetivos, saveObjetivos, rowsToByLine,
} from '../api/objetivosApi.js';
import {
  fetchSeasonBaseline, hasBaselineData,
  COMPARABLE_SEASONS, type SeasonBaseline, type ComparableSeason,
} from '../api/ss26Baseline.js';
import logoUrl from '../assets/losan-logo.png';

const eurFmt = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const LINE_LABELS: Record<SS27Line, string> = {
  MAN: 'Man', WOMAN: 'Woman', TEEN: 'Teen', KIDS: 'Kids', BABY: 'Baby', NEWBORN: 'Newborn',
};

type Metric = 'facturacion' | 'clientes' | 'clientesNuevos';

interface Props {
  agentId: string;
  agentName: string;
}

/**
 * SS27 questionnaire: revenue forecast, expected clients and new clients per
 * line, plus a free-text needs answer. Offers an "autocompletar con SS26 + X%"
 * shortcut that pre-fills revenue and client counts from the agent's SS26
 * history scaled by an increment.
 */
export function ObjetivoSS27Form({ agentId, agentName }: Props) {
  const [obj, setObj] = useState<ObjetivosByLine>(emptyObjetivos());
  const [necesidades, setNecesidades] = useState('');
  const [baseline, setBaseline] = useState<SeasonBaseline | null>(null);
  const [comparableSeason, setComparableSeason] = useState<ComparableSeason>('SS26');
  const [incremento, setIncremento] = useState(10);
  const [status, setStatus] = useState<'loading' | 'ready' | 'saving' | 'saved' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Saved answers load once per agent.
  useEffect(() => {
    let alive = true;
    setStatus('loading');
    fetchObjetivos(agentId).catch(() => ({ rows: [], necesidades: [] })).then((saved) => {
      if (!alive) return;
      if (saved.rows.length > 0) setObj(rowsToByLine(saved.rows));
      if (saved.necesidades[0]?.necesidades) setNecesidades(saved.necesidades[0].necesidades);
      setStatus('ready');
    });
    return () => { alive = false; };
  }, [agentId]);

  // Baseline re-fetches whenever the agent or the comparable season changes.
  useEffect(() => {
    let alive = true;
    setBaseline(null);
    fetchSeasonBaseline(agentId, comparableSeason).catch(() => null).then((base) => {
      if (alive) setBaseline(base);
    });
    return () => { alive = false; };
  }, [agentId, comparableSeason]);

  function setCell(line: SS27Line, metric: Metric, raw: string) {
    const n = Number(raw.replace(/[^\d]/g, ''));
    setObj((o) => ({ ...o, [line]: { ...o[line], [metric]: Number.isFinite(n) ? n : 0 } }));
    setStatus('ready');
  }

  function applyAutocomplete() {
    if (!baseline) return;
    const factor = 1 + (Number.isFinite(incremento) ? incremento : 0) / 100;
    setObj((o) => {
      const next = { ...o };
      for (const l of SS27_LINES) {
        const b = baseline[l];
        if (b.facturacion > 0 || b.clientes > 0) {
          next[l] = {
            ...next[l],
            facturacion: Math.round(b.facturacion * factor),
            clientes: Math.round(b.clientes * factor),
          };
        }
      }
      return next;
    });
    setStatus('ready');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('saving');
    setErrorMsg(null);
    try {
      await saveObjetivos(agentId, agentName, obj, necesidades);
      setStatus('saved');
    } catch (err) {
      setErrorMsg((err as Error).message);
      setStatus('error');
    }
  }

  const totalFact = SS27_LINES.reduce((s, l) => s + (obj[l].facturacion || 0), 0);
  const totalCli = SS27_LINES.reduce((s, l) => s + (obj[l].clientes || 0), 0);
  const totalNew = SS27_LINES.reduce((s, l) => s + (obj[l].clientesNuevos || 0), 0);
  const baselineReady = baseline !== null && hasBaselineData(baseline);

  if (status === 'loading') {
    return (
      <div className="app"><div className="loading">Cargando cuestionario SS27…</div></div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div>
          <img className="header-logo" src={logoUrl} alt="LOSAN" />
          <h1>Objetivo SS27</h1>
          <div className="agent">{agentName} · Cuestionario de previsión de campaña</div>
        </div>
      </header>

      <div className="ss27-autocomplete section no-export">
        <div>
          <strong>Autocompletar con temporada comparable</strong>
          <p>Elige la temporada comparable y el % de crecimiento deseado. Rellenamos la previsión de ventas y el nº de clientes a partir de esa campaña.</p>
        </div>
        <div className="ss27-autocomplete-controls">
          <label>
            Temporada comparable
            <div className="ss27-season-toggle" role="group" aria-label="Temporada comparable">
              {COMPARABLE_SEASONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={comparableSeason === s ? 'active' : ''}
                  aria-pressed={comparableSeason === s}
                  onClick={() => setComparableSeason(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </label>
          <label>
            Crecimiento
            <div className="ss27-pct-wrap">
              <input
                type="number"
                value={incremento}
                onChange={(e) => setIncremento(Number(e.target.value))}
                step={5}
              />
              <span>%</span>
            </div>
          </label>
          <button type="button" onClick={applyAutocomplete} disabled={!baselineReady}>Aplicar</button>
        </div>
        {!baselineReady && (
          <p className="ss27-season-empty">
            {baseline === null
              ? 'Cargando datos de la temporada…'
              : `Sin ventas registradas en ${comparableSeason} para este agente. Prueba con otra temporada.`}
          </p>
        )}
      </div>

      <form onSubmit={onSubmit}>
        {/* Q1 — Revenue forecast */}
        <Question num={1} total={4} title="¿Cuál es tu previsión de ventas de LOSAN para SS27?" hint="Importe estimado en euros (€) por línea de producto.">
          <MetricGrid obj={obj} metric="facturacion" unit="€" onChange={setCell} baseline={baseline} baselineKey="facturacion" season={comparableSeason} />
          <TotalRow label="Total facturación prevista" value={eurFmt.format(totalFact)} />
        </Question>

        {/* Q2 — Expected clients */}
        <Question num={2} total={4} title="¿A cuántos clientes crees que venderás la colección SS27?" hint="Número de clientes por línea de producto.">
          <MetricGrid obj={obj} metric="clientes" onChange={setCell} baseline={baseline} baselineKey="clientes" season={comparableSeason} />
          <TotalRow label="Total clientes previstos" value={totalCli.toLocaleString('es-ES')} />
        </Question>

        {/* Q3 — New / reactivated clients */}
        <Question num={3} total={4} title="¿Cuántos clientes nuevos crees que podrás crear o reactivar para SS27?" hint="Número total por línea de producto.">
          <MetricGrid obj={obj} metric="clientesNuevos" onChange={setCell} />
          <TotalRow label="Total clientes nuevos / reactivados" value={totalNew.toLocaleString('es-ES')} />
        </Question>

        {/* Q4 — Free text */}
        <Question num={4} total={4} title="¿Qué necesitas por parte de LOSAN para alcanzar tu mejor nivel de ventas?">
          <textarea
            className="ss27-textarea"
            value={necesidades}
            onChange={(e) => { setNecesidades(e.target.value); setStatus('ready'); }}
            placeholder="Escribe aquí tu respuesta…"
            rows={5}
          />
        </Question>

        {errorMsg && <div className="login-error" role="alert">{errorMsg}</div>}
        {status === 'saved' && <div className="ss27-saved" role="status">✓ Respuestas guardadas correctamente</div>}

        <button type="submit" className="ss27-submit" disabled={status === 'saving'}>
          {status === 'saving' ? 'Guardando…' : 'Guardar respuestas'}
        </button>
      </form>
    </div>
  );
}

function Question({ num, total, title, hint, children }: {
  num: number; total: number; title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="section ss27-question">
      <div className="ss27-q-eyebrow">Pregunta {num} de {total}</div>
      <h2 className="ss27-q-title">{title}</h2>
      {hint && <p className="ss27-q-hint">{hint}</p>}
      {children}
    </div>
  );
}

function MetricGrid({ obj, metric, unit, onChange, baseline, baselineKey, season }: {
  obj: ObjetivosByLine;
  metric: Metric;
  unit?: string;
  onChange: (line: SS27Line, metric: Metric, raw: string) => void;
  baseline?: SeasonBaseline | null;
  baselineKey?: 'facturacion' | 'clientes';
  season?: ComparableSeason;
}) {
  return (
    <div className="ss27-grid">
      {SS27_LINES.map((line) => {
        const base = baseline && baselineKey ? baseline[line][baselineKey] : 0;
        return (
          <label key={line} className="ss27-field">
            <span>{LINE_LABELS[line]}</span>
            <div className="ss27-input-wrap">
              <input
                type="text"
                inputMode="numeric"
                value={obj[line][metric] ? obj[line][metric].toLocaleString('es-ES') : ''}
                onChange={(e) => onChange(line, metric, e.target.value)}
                placeholder="0"
              />
              {unit && <span className="ss27-unit">{unit}</span>}
            </div>
            {base > 0 && (
              <span className="ss27-baseline-hint">
                {season ?? 'SS26'}: {baselineKey === 'facturacion' ? eurFmt.format(base) : base.toLocaleString('es-ES')}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="ss27-total">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
